import { fg } from '@atlaskit/platform-feature-flags';

import { serveFpsMultiSiteSearchExperiment } from '../experiments';

/**
 * An AGG site ARI. Structurally the same as search-client's `SearchEntitiesProductARI`, declared
 * here so that this helper does not depend on that package.
 */
export type SiteARI = `ari:cloud:${string}::${string}`;

/**
 * Builds the AGG site ARI used as the `cloudIdARI` variable / `locations` value for search
 * GraphQL queries.
 *
 * `primaryProduct` is typed as `string` rather than a product-key union: the consuming packages
 * each own a `PrimaryProductKey` that this package cannot import without a cycle. Tighten once a
 * shared product-key type exists.
 */
export const getCloudIdARI = (cloudId: string, primaryProduct: string | undefined): SiteARI => {
	if (serveFpsMultiSiteSearchExperiment({ primaryProduct, fireExperimentExposure: false })) {
		return `ari:cloud:platform::site/${cloudId}`;
	}

	return primaryProduct === 'jira' && fg('jira_site_ari_query_enabled')
		? `ari:cloud:jira::site/${cloudId}`
		: `ari:cloud:confluence::site/${cloudId}`;
};

/**
 * Builds the array of AGG site ARIs used as the `cloudIdARIs` variable / `locations` value for
 * search GraphQL queries. The current site is kept first and duplicates are removed.
 */
export const getCloudIdARIs = (
	cloudId: string,
	primaryProduct: string | undefined,
	additionalCloudIds: readonly string[] = [],
): SiteARI[] => {
	const cloudIds = [cloudId, ...additionalCloudIds].filter(
		(id, index, all) => Boolean(id) && all.indexOf(id) === index,
	);

	return cloudIds.map((id) => getCloudIdARI(id, primaryProduct));
};
